import { createListSearchParams, ListPagination } from 'api/list-params'
import { TTermImportItems } from 'api/terms'
import { pipe } from 'fp-ts/function'
import { string } from 'io-ts'
import * as t from 'io-ts'
import { formatToKebabCseDateFormat } from 'lib/format/date'
import {
  decodeJson,
  decodeJsonWithTotal,
  del,
  get,
  patch,
  post,
  put,
} from 'lib/request'

import {
  ProjectType,
  TChatGPTTranslation,
  TGptSuggestion,
  TProject,
  TProjectFigmaDetails,
  TProjectLanguageActivityList,
  TProjectRepo,
  TProjectReposList,
  TProjectUpdates,
  TTagsList,
  TTeamMembersList,
  UserPermissionType,
} from './projects.codecs'

export type GetProjectsInput = {
  pagination: ListPagination
  ownerId?: string
}

export const getProjects = async ({
  pagination,
  ownerId,
}: GetProjectsInput) => {
  const params = createListSearchParams({ pagination })

  if (ownerId) params.set('ownerid', ownerId)

  return pipe(
    await get('api/projects', {
      query: params,
    }),
    decodeJsonWithTotal(TProject, pagination),
  )
}

export type GetProjectDetailsInput = { projectId: string }

export const getProjectDetails = async ({
  projectId,
}: GetProjectDetailsInput) => {
  return pipe(
    await get('api/projects/:projectId', {
      params: { projectId },
    }),
    decodeJson(TProject),
  )
}

export const editProjectDetails = async ({
  projectId,
  name,
  description,
}: {
  projectId: string
  name: string
  description: string
}) => {
  return pipe(
    await put('api/projects/:projectId', {
      params: { projectId },
      body: {
        name,
        description,
      },
    }),
  )
}

export const editProjectPlaceholders = async ({
  projectId,
  base,
  namedLeft,
  namedRight,
}: {
  projectId: string
  base?: string
  namedLeft?: string
  namedRight?: string
}) => {
  return pipe(
    await patch('api/projects/:projectId/placeholders', {
      params: { projectId },
      body: {
        ...(base !== undefined && {
          base,
        }),

        ...(namedLeft !== undefined && {
          namedLeft,
        }),

        ...(namedRight !== undefined && {
          namedRight,
        }),
      },
    }),
  )
}

export const editProjectLanguages = async ({
  projectId,
  languages,
}: {
  projectId: string
  languages: Array<string>
}) => {
  return pipe(
    await patch('api/projects/:projectId/languages', {
      params: { projectId },
      body: {
        languages,
      },
    }),
  )
}

export const editProjectMainLanguage = async ({
  projectId,
  mainLanguage,
}: {
  projectId: string
  mainLanguage: string
}) => {
  return pipe(
    await patch('api/projects/:projectId/main-language', {
      params: { projectId },
      body: {
        mainLanguage,
      },
    }),
  )
}

export const updateProjectAvatar = async ({
  projectId,
  file,
}: {
  projectId: string
  file: Blob
}) => {
  return pipe(
    await patch('api/projects/:projectId/avatar', {
      params: { projectId },
      type: 'file',
      body: {
        file,
      },
    }),
  )
}

export type GetProjectLanguageActivityListInput = {
  projectId: string
  dateFrom: string
  delimiter: 'DAY' | 'WEEK' | 'MONTH'
}

export const getProjectLanguageActivity = async ({
  projectId,
  dateFrom,
  delimiter,
}: GetProjectLanguageActivityListInput) => {
  const params = new URLSearchParams()

  params.set('date_from', dateFrom)
  params.set('date_to', formatToKebabCseDateFormat(new Date()))
  params.set('delimiter', delimiter)

  return pipe(
    await get('api/projects/:projectId/activity', {
      params: { projectId },
      query: params,
    }),
    decodeJson(TProjectLanguageActivityList),
  )
}

export const createProject = async ({
  name,
  description,
  type,
  mainLanguage,
  languages,
}: {
  name: string
  type: ProjectType
  description: string
  mainLanguage: string
  languages: Array<string>
}) => {
  return pipe(
    await post('api/projects', {
      body: {
        name,
        type,
        description,
        mainLanguage,
        languages,
      },
    }),
    decodeJson(TProject),
  )
}

export const markProjectAsFavorite = async ({
  projectId,
}: {
  projectId: string
}) => {
  return pipe(
    await patch('api/projects/:projectId/favorite', {
      params: { projectId },
    }),
  )
}

export type GetProjectTagsInput = {
  projectId: string
}

export const getProjectTags = async ({ projectId }: GetProjectTagsInput) => {
  return pipe(
    await get('api/projects/:projectId/tags', {
      params: { projectId },
    }),
    decodeJson(TTagsList),
  )
}

export type GetProjectUsersInput = {
  projectId: string
}

export const getProjectUsers = async ({ projectId }: GetProjectUsersInput) => {
  return pipe(
    await get('api/projects/:projectId/team', {
      params: { projectId },
    }),
    decodeJson(TTeamMembersList),
  )
}

export const inviteUser = async ({
  projectId,
  ...body
}: {
  projectId: string
  email: string
  fullName: string
  languages: Array<string>
  permission: UserPermissionType
}) => {
  return pipe(
    await post('api/projects/:projectId/team', {
      params: { projectId },
      body,
    }),
  )
}

export const editUser = async ({
  projectId,
  userId,
  languages,
  permission,
}: {
  projectId: string
  userId: string
  languages: Array<string>
  permission: UserPermissionType
}) => {
  return pipe(
    await patch('api/projects/:projectId/team/:userId/permission', {
      params: { projectId, userId },
      body: {
        languages,
        permission,
      },
    }),
  )
}

export const unsubscribeFromProject = async ({
  projectId,
}: {
  projectId: string
}) => {
  return pipe(
    await del('api/projects/:projectId/unsubscribe', {
      params: { projectId },
    }),
  )
}

export const deleteProject = async ({ projectId }: { projectId: string }) => {
  return pipe(
    await del('api/projects/:projectId', {
      params: { projectId },
    }),
  )
}

export const removeUserFromProject = async ({
  projectId,
  userId,
}: {
  projectId: string
  userId: string
}) => {
  return pipe(
    await del('api/projects/:projectId/team/:userId', {
      params: { projectId, userId },
    }),
  )
}

export const changeUserStatus = async ({
  projectId,
  userId,
  status,
}: {
  projectId: string
  userId: string
  status: string
}) => {
  return pipe(
    await patch('api/projects/:projectId/team/:userId/status', {
      params: { projectId, userId },
      body: { status },
    }),
  )
}

export const changeProjectOwner = async ({
  projectId,
  userId,
}: {
  projectId: string
  userId: string
}) => {
  return pipe(
    await post('api/projects/:projectId/team/:userId/owner', {
      params: { projectId, userId },
    }),
  )
}

export const getGhatGPTTranslation = async ({
  projectId,
  termId,
  lang,
}: {
  projectId: string
  termId: string
  lang: string
}) => {
  return pipe(
    await get('api/projects/:projectId/terms/:termId/gpt-translation', {
      params: { projectId, termId },
      query: new URLSearchParams({ lang }),
    }),
    decodeJson(TChatGPTTranslation),
  )
}

// GITLAB
export type GetGitlabIntegrationLinkInput = {
  projectId: string
}

export const getGitlabIntegrationLink = async ({
  projectId,
}: GetGitlabIntegrationLinkInput) => {
  return pipe(
    await get('api/projects/:projectId/repos/gitlab/oauth/link', {
      params: { projectId },
    }),
    decodeJson(t.string),
  )
}

export type CreateGitlabIntegrationInput = {
  projectId: string
  code: string
}

export const createGitlabIntegration = async ({
  projectId,
  code,
}: CreateGitlabIntegrationInput) => {
  return pipe(
    await post('api/projects/:projectId/repos/gitlab', {
      params: { projectId },
      body: {
        code,
      },
    }),
    decodeJson(TProjectRepo),
  )
}

export type GetGitLabRepositoriesInput = {
  projectId: string
  repoId: string
  pagination: ListPagination
  search?: string
}

export const getGitLabRepositories = async ({
  projectId,
  repoId,
  pagination,
  search,
}: GetGitLabRepositoriesInput) => {
  const params = createListSearchParams({ pagination })

  if (search) {
    params.set('search', search)
  }

  return pipe(
    await get('api/projects/:projectId/repos/:repoId/repositories', {
      params: { projectId, repoId },
      query: params,
    }),
    decodeJsonWithTotal(string, pagination),
  )
}

export type GetGitLabRepositoryBranchesInput = {
  projectId: string
  repoId: string
  repoName: string
  pagination: ListPagination
  search?: string
}

export const getGitLabRepositoryBranches = async ({
  projectId,
  repoId,
  repoName,
  pagination,
  search,
}: GetGitLabRepositoryBranchesInput) => {
  const params = createListSearchParams({ pagination })
  if (search) {
    params.set('search', search)
  }

  params.set('repo_name', repoName)

  return pipe(
    await get('api/projects/:projectId/repos/:repoId/repositories/branches', {
      params: { projectId, repoId },
      query: params,
    }),
    decodeJsonWithTotal(string, pagination),
  )
}

type UpdateGitLabRepositoryInput = {
  projectId: string
  repoId: string
  baseBranch: string
  pathTemplate: string
  repo: string
}

export const updateGitLabRepository = async ({
  projectId,
  repoId,
  baseBranch,
  pathTemplate,
  repo,
}: UpdateGitLabRepositoryInput) => {
  return pipe(
    await put('api/projects/:projectId/repos/:repoId', {
      params: { projectId, repoId },
      body: {
        baseBranch,
        pathTemplate,
        repo,
      },
    }),
    decodeJson(TProjectRepo),
  )
}

export const updateGitRepository = async ({
  projectId,
  repoId,
  baseBranch,
  pathTemplate,
}: {
  projectId: string
  repoId: string
  baseBranch: string
  pathTemplate: string
}) => {
  return pipe(
    await patch('api/projects/:projectId/repos/:repoId', {
      params: { projectId, repoId },
      body: {
        baseBranch,
        pathTemplate,
      },
    }),
  )
}

// GITHUB
export type GetProjectReposInput = {
  projectId: string
}

export const getProjectRepos = async ({ projectId }: GetProjectReposInput) => {
  const params = new URLSearchParams()

  params.set('completed', 'true')

  return pipe(
    await get('api/projects/:projectId/repos', {
      params: { projectId },
      query: params,
    }),
    decodeJson(TProjectReposList),
  )
}

export type GetGitHubRepositoriesInput = {
  username: string
  pagination: ListPagination
  search?: string
}

export const getGitHubRepositories = async ({
  username,
  pagination,
  search,
}: GetGitHubRepositoriesInput) => {
  const params = createListSearchParams({ pagination })

  if (search) {
    params.set('search', search)
  }

  return pipe(
    await get('api/github/:username/repositories', {
      params: { username },
      query: params,
    }),
    decodeJsonWithTotal(string, pagination),
  )
}

export type GetGitHubRepositoryBranchesInput = {
  username: string
  repoName: string
  pagination: ListPagination
  search?: string
}

export const getGitHubRepositoryBranches = async ({
  username,
  repoName,
  pagination,
  search,
}: GetGitHubRepositoryBranchesInput) => {
  const params = createListSearchParams({ pagination })

  if (search) {
    params.set('search', search)
  }

  return pipe(
    await get('api/github/:username/repositories/:repoName/branches', {
      params: { username, repoName },
      query: params,
    }),
    decodeJsonWithTotal(string, pagination),
  )
}

export type GetProjectGitBranchesInput = {
  projectId: string
  repoId: string
  pagination: ListPagination
  search?: string
}

export const getProjectGitBranches = async ({
  projectId,
  repoId,
  pagination,
  search,
}: GetProjectGitBranchesInput) => {
  const params = createListSearchParams({ pagination })

  if (search) {
    params.set('search', search)
  }

  return pipe(
    await get('api/projects/:projectId/repos/:repoId/branches', {
      params: { projectId, repoId },
      query: params,
    }),
    decodeJsonWithTotal(string, pagination),
  )
}

export type IntegrateProjectWithGitHubRepoProps = {
  projectId: string
  baseBranch: string
  pathTemplate: string
  repo: string
  username: string
}

export const integrateProjectWithGitHubRepo = async ({
  projectId,
  baseBranch,
  repo,
  username,
  pathTemplate,
}: IntegrateProjectWithGitHubRepoProps) => {
  return pipe(
    await post('api/projects/:projectId/repos', {
      params: { projectId },
      body: {
        baseBranch,
        repo,
        username,
        pathTemplate,
      },
    }),
  )
}

export type GetProjectFigmaDetailsProps = {
  projectId: string
}

export const getProjectFigmaDetailsFile = async ({
  projectId,
}: GetProjectFigmaDetailsProps) => {
  return pipe(
    await get('api/projects/:projectId/figma', {
      params: { projectId },
    }),
    decodeJson(TProjectFigmaDetails),
  )
}

export type IntegrateProjectWithFigmaFileProps = {
  projectId: string
  authCode: string
  fileId: string
}

export const integrateProjectWithFigmaFile = async ({
  projectId,
  authCode,
  fileId,
}: IntegrateProjectWithFigmaFileProps) => {
  return pipe(
    await post('api/projects/:projectId/figma', {
      params: { projectId },
      body: {
        authCode,
        fileId,
      },
    }),
  )
}

export const deleteFigmaIntegration = async ({
  projectId,
}: {
  projectId: string
}) => {
  return pipe(
    await del('api/projects/:projectId/figma', {
      params: { projectId },
    }),
  )
}

export const syncTranslationsWithGit = async ({
  projectId,
  repoId,
  branch,
  message,
}: {
  projectId: string
  repoId: string
  branch?: string
  message?: string
}) => {
  return pipe(
    await post('api/projects/:projectId/repos/:repoId/sync', {
      params: { projectId, repoId },
      body: {
        branch,
        message,
      },
    }),
  )
}

export const importTranslationsFormGit = async ({
  projectId,
  repoId,
  branch,
  override,
  lang,
}: {
  projectId: string
  repoId: string
  branch: string
  override?: boolean
  lang?: Array<string>
}) => {
  const searchParams = new URLSearchParams()

  if (override !== undefined) {
    searchParams.set('override', override.toString())
  }

  if (lang !== undefined) {
    for (const langCode of lang) {
      searchParams.append('lang', langCode)
    }
  }

  searchParams.set('branch', branch)

  return pipe(
    await post('api/projects/:projectId/repos/:repoId/import', {
      params: { projectId, repoId },
      query: searchParams,
    }),
    decodeJson(TTermImportItems),
  )
}

export const deleteRepoIntegration = async ({
  projectId,
  repoId,
}: {
  projectId: string
  repoId: string
}) => {
  return pipe(
    await del('api/projects/:projectId/repos/:repoId', {
      params: { projectId, repoId },
    }),
  )
}

export type GetProjectUpdatesInput = {
  projectId: string
}

export const getProjectUpdates = async ({
  projectId,
}: GetProjectUpdatesInput) => {
  return pipe(
    await get('api/projects/:projectId/updates', {
      params: { projectId },
    }),
    decodeJson(TProjectUpdates),
  )
}

export type GetSuggestionForTranslationInput = {
  projectId: string
  lang: string
  text: string
}

export const getSuggestionForTranslation = async ({
  projectId,
  lang,
  text,
}: GetSuggestionForTranslationInput) => {
  return pipe(
    await post('api/projects/:projectId/terms/gpt-suggestion', {
      params: { projectId },
      body: {
        lang,
        text,
      },
    }),
    decodeJson(TGptSuggestion),
  )
}

export const linkProject = async ({
  projectId,
  projectIdToLink,
}: {
  projectId: string
  projectIdToLink: string
}) => {
  return pipe(
    await patch('api/projects/:projectId/linked', {
      params: { projectId },
      body: {
        projectId: projectIdToLink,
      },
    }),
  )
}

export const createUserActivityForClosingLastUpdates = async ({
  projectId,
}: {
  projectId: string
}) => {
  return pipe(
    await post('api/projects/:projectId/user_activity', {
      params: { projectId },
    }),
  )
}

export const editProjectIndent = async ({
  projectId,
  json,
  xml,
}: {
  projectId: string
  json: number
  xml: number
}) => {
  return pipe(
    await patch('api/projects/:projectId/indent', {
      params: { projectId },
      body: {
        json,
        xml,
      },
    }),
  )
}
